import type {
  ExperienceContent,
  ExperienceDraftContent,
  ExperiencePage,
  ExperiencePageDraft,
} from '../schemas/experience.js';
import {
  NOYAU_TOKEN,
  type NoyauBlockMode,
  type NoyauChoiceMode,
  type NoyauCode,
  oppositeNoyau,
} from '../schemas/noyau.js';
import { DEFAULT_VERSION_PIN, type VersionPin } from '../schemas/versioning.js';
import { blockVariableTexts } from './block-texts.js';

/**
 * Where the noyau SEQUENCE sits in a timeline (noyau.md §3), read the same way
 * by every rule that needs it: the refusals of §3.4, the reach of `@noyau`
 * (§3.3), the playability of a page when the sequence is skipped (§4.5) and the
 * reference to the texts (§3.5).
 *
 * Pure, and below `variables.ts`, `problems.ts` and `play.ts` on purpose: all
 * three read it, and it reads none of them - so « which page is the first
 * choice » has one answer for the palette, the save box, the server and the
 * reader.
 */

type AnyContent = ExperienceContent | ExperienceDraftContent;
type AnyPage = ExperiencePage | ExperiencePageDraft;

/** One Noyau block of a timeline, and where it sits. */
export type NoyauBlockSite = {
  readonly pageId: string;
  /** Zero-based, like every page index of the refusals; the screen adds one. */
  readonly pageIndex: number;
  readonly pageName: string;
  readonly blockId: string;
  readonly mode: NoyauBlockMode;
};

/** Every Noyau block of a content, in reading order - page by page, temps by temps. */
export function noyauBlocksOf(content: AnyContent): NoyauBlockSite[] {
  const sites: NoyauBlockSite[] = [];
  (content.pages ?? []).forEach((page, pageIndex) => {
    for (const temps of page.temps) {
      for (const block of temps.blocks) {
        if (block.type !== 'noyau') continue;
        sites.push({
          pageId: page.id,
          pageIndex,
          pageName: page.name,
          blockId: block.id,
          mode: block.mode,
        });
      }
    }
  });
  return sites;
}

/**
 * THE first choice of a timeline: the first block in the mode, in reading order,
 * or null when there is none.
 *
 * A second one is a refusal (`noyau-choice-repeated`), so a version has at most
 * one; a draft may hold two while somebody composes, and the FIRST is the one
 * the rules measure the rest against - the one a session would play first.
 */
export function firstChoiceOf(content: AnyContent): NoyauBlockSite | null {
  return noyauBlocksOf(content).find((site) => site.mode === 'first-choice') ?? null;
}

/**
 * Whether a page DEPENDS on the noyau (§4.5): it carries a Noyau block,
 * whatever its mode, or one of its blocks cites `@noyau` in a text.
 *
 * Such a page stops being PLAYABLE when the sequence is skipped - `no-extracts`
 * or `failed` - at the régime of the empty protocol page (`isPlayablePage`).
 *
 * A DISPLAY CONDITION on `@noyau` does not make a page depend on it, and that is
 * the reading of §3.6 : « un bloc conditionné par le noyau est masqué, comme
 * toute condition sur une variable vide, et la page qui le porte est de toute
 * façon sautée SI elle dépend du noyau ». A condition is made to meet an empty
 * variable - the block it guards simply does not show - so a page of thanks that
 * carries one Souverain-only sentence is still a page of thanks for everybody.
 */
export function pageDependsOnNoyau(page: AnyPage): boolean {
  // The context of a page played in the conversation is a text of the page like
  // the others (prolongation.md §3.2): citing `@noyau` there is depending on it.
  if (page.conversation?.context.includes(NOYAU_TOKEN)) return true;
  return page.temps.some((temps) =>
    temps.blocks.some(
      (block) =>
        block.type === 'noyau' ||
        blockVariableTexts(block).some((text) => text.includes(NOYAU_TOKEN)),
    ),
  );
}

/** The block of choice of ONE page, its mode, and its gap threshold. */
export type PageNoyauChoice = {
  readonly blockId: string;
  readonly mode: NoyauChoiceMode;
  /**
   * The block's gap threshold (§3.8, issue #169), whatever the mode: the deal
   * of a FIRST choice freezes it on its row, a second choice reads none (N68).
   */
  readonly maxGap: number;
};

/**
 * The block of CHOICE a page carries - the first Noyau block in a choice mode,
 * in reading order - or null when it carries none (§3.2, §5.1).
 *
 * A version holds at most one per page (`noyau-choice-page-exit` refuses a
 * second), so « the first » only ever matters to a draft, and it is the one the
 * reader, the server and the previews all read: the page's way out.
 */
export function pageNoyauChoiceOf(page: AnyPage): PageNoyauChoice | null {
  for (const temps of page.temps) {
    for (const block of temps.blocks) {
      if (block.type === 'noyau' && block.mode !== 'portrait') {
        return { blockId: block.id, mode: block.mode, maxGap: block.maxGap };
      }
    }
  }
  return null;
}

/** Whether a page SHOWS the portrait: it carries a Noyau block in portrait mode (§3.2). */
export function pageShowsPortrait(page: AnyPage): boolean {
  return page.temps.some((temps) =>
    temps.blocks.some((block) => block.type === 'noyau' && block.mode === 'portrait'),
  );
}

/**
 * What a choice is played between (§3.2, §5.1, N18): the two noyaux of its
 * PAIR, in the order of the RANKING - the order the cards are SHOWN in is drawn
 * apart, at the first arrival on the page.
 *
 * - the first choice faces the PRINCIPAL and the SECOND of the determination;
 * - the second choice faces the RETAINED noyau and its OPPOSITE, the mirror of
 *   §2.2 - which may be the very second of the first choice (principal SGP,
 *   second PGS): the same pair is then replayed on the second text, and that is
 *   permitted and recorded as it is (N18). The retained noyau handed in is the
 *   one `noyauRetention` reads (issue #169): beyond the gap threshold, the
 *   principal and its mirror, even when the person took the second (N72).
 *
 * Null when what the pair needs is not there yet: no ranking for the first
 * choice, no retained noyau for the second. One function for the server, which
 * deals the cards, and for the two previews, which play a pair of example
 * (N20): three spellings of « who faces whom » would be three sequences.
 */
export function noyauChoicePair(
  mode: NoyauChoiceMode,
  standing: {
    readonly principal: NoyauCode | null;
    readonly second: NoyauCode | null;
    readonly retained: NoyauCode | null;
  },
): readonly [NoyauCode, NoyauCode] | null {
  if (mode === 'first-choice') {
    const { principal, second } = standing;
    return principal === null || second === null ? null : [principal, second];
  }
  const { retained } = standing;
  return retained === null ? null : [retained, oppositeNoyau(retained)];
}

/**
 * The pin the texts of the noyaux are RESOLVED at for this content (§3.5,
 * vitrine.md §3.1), or null when there is nothing to resolve.
 *
 * Null for a content without a Noyau block, WHATEVER its `noyauTexts` says: an
 * experience that plays no sequence reads no text, and a stale reference left in
 * a hand-written content must not refuse its start-up for texts it never shows.
 * `latest` for a content that carries a Noyau block and no reference - the
 * default of every reference (V16), which the editor writes explicitly and a
 * hand-written content may forget.
 *
 * The ONE reading of the reference, for the cascade of the start-up, « utilisé
 * dans » and « Passer en production » alike.
 */
export function noyauTextsPinOf(content: AnyContent): VersionPin | null {
  if (noyauBlocksOf(content).length === 0) return null;
  return content.noyauTexts?.pin ?? DEFAULT_VERSION_PIN;
}
